import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Plus, Edit, Trash2, Eye, Pin } from 'lucide-react';
import { toast } from 'sonner';
import ProjectModal from '../components/modals/ProjectModal';
import ProjectDetailModal from '../components/modals/ProjectDetailModal';
import TopAlert from '../components/TopAlert';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currency';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';

const Projects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectFinances, setProjectFinances] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [pageAlert, setPageAlert] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pinnedIds, setPinnedIds] = useState([]);
  const perPage = 6;
  const router = useNavigate();
  
  useEffect(() => {
    fetchProjects();
    const storedPins = JSON.parse(localStorage.getItem('pinnedProjects') || '[]');
    setPinnedIds(storedPins);
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
      setPageAlert('');

      // Fetch finances for each project
      const financePromises = response.data.map(project =>
        apiClient.get(`/projects/${project.project_id}/finances`).catch(() => null)
      );
      const financeResults = await Promise.all(financePromises);

      const financeMap = {};
      financeResults.forEach((result, index) => {
        if (result) {
          financeMap[response.data[index].project_id] = result.data;
        }
      });
      setProjectFinances(financeMap);
    } catch (error) {
      setPageAlert('Failed to load projects. Please refresh and try again.');
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedProject(null);
    setModalOpen(true);
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiClient.delete(`/projects/${projectToDelete.project_id}`);
      toast.success('Project deleted successfully');
      fetchProjects();
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'acquired': 'bg-slate-100 text-slate-800',
      'ongoing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-emerald-100 text-emerald-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const isProjectDelayed = (project) => {
    if (project.status !== 'ongoing' || !project.end_date) return false;
    return new Date(project.end_date) < new Date();
  };

  const handleViewDetails = (project) => {
    // setSelectedProject(project);
    // router.push(`/projects/${project.project_id}`);
    router(`/projects/${project.project_id}`);
    // setDetailModalOpen(true);
  };

  const handleTogglePin = (projectId) => {
    const projectKey = String(projectId);
    const nextPinned = pinnedIds.includes(projectKey)
      ? pinnedIds.filter(id => id !== projectKey)
      : [...pinnedIds, projectKey];

    localStorage.setItem('pinnedProjects', JSON.stringify(nextPinned));
    setPinnedIds(nextPinned);
  };

  const filteredProjects = projects
    .map((project, index) => ({ project, index }))
    .filter(({ project }) => {
      const query = searchQuery.trim().toLowerCase();
      if (!query) return true;
      return (
        project.title?.toLowerCase().includes(query) ||
        project.description?.toLowerCase().includes(query) ||
        project.status?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      const aPinned = pinnedIds.includes(String(a.project.project_id));
      const bPinned = pinnedIds.includes(String(b.project.project_id));
      if (aPinned === bPinned) return a.index - b.index;
      return aPinned ? -1 : 1;
    })
    .map(({ project }) => project);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * perPage;
  const pagedProjects = filteredProjects.slice(startIndex, startIndex + perPage);

  if (loading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <Skeleton className="h-9 w-40" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-slate-100">
              <CardHeader>
                <div className="flex justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-96" />
                  </div>
                  <div className="flex space-x-2">
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-8 w-8 rounded" />
                    <Skeleton className="h-8 w-8 rounded" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div className="flex space-x-6">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="space-y-1">
                        <Skeleton className="h-3 w-12" />
                        <Skeleton className="h-5 w-20" />
                      </div>
                    ))}
                  </div>
                  <Skeleton className="h-2 w-64 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6" data-testid="projects-page">
        <TopAlert message={pageAlert} tone="error" onClose={() => setPageAlert('')} />
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Projects</h1>
            <p className="text-slate-600 mt-1">Track and manage your projects</p>
          </div>
          <Button
            onClick={handleAdd}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-project-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            placeholder="Search projects by title, status, or description"
            className="w-full md:max-w-md px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
          />
          <div className="text-sm text-slate-500">
            Showing {filteredProjects.length === 0 ? 0 : startIndex + 1} - {Math.min(startIndex + perPage, filteredProjects.length)} of {filteredProjects.length}
          </div>
        </div>

        {filteredProjects.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-12">
              <div className="text-center">
                <p className="text-slate-500 mb-4">
                  {projects.length === 0 ? 'No projects yet. Create your first project!' : 'No projects match your search.'}
                </p>
                <Button
                  onClick={handleAdd}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {pagedProjects.map((project) => (
              <Card
                key={project.project_id}
                className="border-slate-100 hover:shadow-lg transition-shadow cursor-pointer"
                data-testid={`project-card-${project.project_id}`}
                onClick={() => handleViewDetails(project)}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <CardTitle className="text-xl">{project.title}</CardTitle>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}
                        >
                          {project.status}
                        </span>
                        {isProjectDelayed(project) && (
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-200">
                            Delayed
                          </span>
                        )}
                        {pinnedIds.includes(String(project.project_id)) && (
                          <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                            Pinned
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-2">{project.description}</p>
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleTogglePin(project.project_id); }}
                        data-testid={`pin-project-${project.project_id}`}
                      >
                        <Pin className={`h-4 w-4 ${pinnedIds.includes(String(project.project_id)) ? 'fill-amber-500 text-amber-600' : ''}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleViewDetails(project); }}
                        data-testid={`view-project-${project.project_id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEdit(project); }}
                        data-testid={`edit-project-${project.project_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(project); }}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-project-${project.project_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-6">
                      <div>
                        <p className="text-xs text-slate-500">Budget</p>
                        <p className="text-lg font-semibold text-slate-900">
                          {getCurrencySymbol(user?.currency)}{project.total_budget.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Received</p>
                        <p className="text-lg font-semibold text-emerald-600">
                          {getCurrencySymbol(user?.currency)}{projectFinances[project.project_id]?.total_received?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Pending</p>
                        <p className="text-lg font-semibold text-amber-600">
                          {getCurrencySymbol(user?.currency)}{projectFinances[project.project_id]?.total_pending?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Progress</p>
                        <p className="text-lg font-semibold text-slate-900">
                          {project.progress_percentage}%
                        </p>
                      </div>
                    </div>
                    <div className="w-full max-w-xs">
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className="bg-indigo-600 h-2 rounded-full transition-all"
                          style={{ width: `${project.progress_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {filteredProjects.length > perPage && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-slate-500">Page {safePage} of {totalPages}</div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <ProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        project={selectedProject}
        onSuccess={fetchProjects}
      />

      <ProjectDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        project={selectedProject}
        onSuccess={fetchProjects}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {projectToDelete?.title}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Projects;
